/**
 * @file LeaderboardPage.jsx
 * @description Standalone leaderboard page with Material Design 3 styling
 */

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useUserAuth } from "../../UserAuth";
import { db } from "../../firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { ChallengeLeaderboard } from "./ChallengeLeaderboard";
import { MaterialHeader } from "../MaterialHeader";
import { Icon } from "../Icon";
import { useViewportHeight } from "../../utils/viewportUtils";
import { getDatesToChapters } from "../challenges/challenges";
import { getReadingPortionsFromBooks } from "../../utils/bible_utils";
import { parseDateList } from "../../utils/dateUtils";

export const LeaderboardPage = () => {
  const [challengeList, setChallengeList] = useState([]);
  const [loadedChallenges, setLoadedChallenges] = useState(false);
  const { user } = useUserAuth();
  const viewportHeight = useViewportHeight();
  const history = useHistory();

  const FetchChallenges = async (retryCount = 0) => {
    if (user === null) {
      return;
    }

    const maxRetries = 3;
    
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 7000)
      );

      const userChallengesRef = collection(db, "users", user.uid, "challenges");
      const fetchPromise = getDocs(userChallengesRef);
      const userChallengesDoc = await Promise.race([fetchPromise, timeoutPromise]);

      const challengesPromises = userChallengesDoc.docs.map(async (doc) => {
        let data = await FetchChallengeData(doc.id);
        const readingPortions = getReadingPortionsFromBooks(data.books || []);
        const datesToChapters = getDatesToChapters(
          data.start_date,
          data.end_date || "",
          data.days || [],
          readingPortions,
          parseDateList(data.skip_dates || "")
        );
        const memberDoc = await getDoc(
          doc(db, "challenges", doc.id, "users", user.uid)
        );
        const isAdmin = memberDoc.exists() && memberDoc.data()?.role === "admin";
        return {
          id: doc.id,
          name: data.challenge_name,
          portion: data.reading_portions,
          challengeType: data.challenge_type || "leaderboard",
          goalPercent: Number.isFinite(data.goal_percent) ? Math.round(data.goal_percent) : null,
          datesToChapters: Array.isArray(datesToChapters) ? datesToChapters : [],
          isAdmin
        };
      });

      const challenges = await Promise.all(challengesPromises);

      setChallengeList(challenges);
      setLoadedChallenges(true);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      
      if (retryCount < maxRetries) {
        console.log(`Retrying FetchChallenges (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => {
          FetchChallenges(retryCount + 1);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
      } else {
        setLoadedChallenges(true); // Show empty state
      }
    }
  };

  const FetchChallengeData = async (id) => {
    const challengeRef = doc(db, "challenges", id);
    const challengeDoc = await getDoc(challengeRef);
    return challengeDoc.data();
  };

  useEffect(() => {
    if (user) {
      FetchChallenges();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    history.push('/');
  };

  if (!user) {
    return (
      <>
        <MaterialHeader 
          variant="close" 
          title="Leaderboard"
          onClose={handleClose}
          hideSettings={true}
        />
        <div style={{
          height: `calc(${viewportHeight} - 64px)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px'
        }}>
          <Icon name="signIn" size="hero" style={{ marginBottom: '16px', color: 'var(--md-sys-color-on-surface-variant)' }} />
          <p style={{ 
            margin: '0', 
            fontSize: '16px',
            color: 'var(--md-sys-color-on-surface-variant)',
            textAlign: 'center'
          }}>
            Log in to view leaderboards!
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MaterialHeader 
        variant="close" 
        title="Leaderboard"
        onClose={handleClose}
        hideSettings={true}
      />
      <div style={{
        height: `calc(${viewportHeight} - 64px)`,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center', 
          padding: '24px 16px 0 16px',
          flexShrink: 0
        }}>
          <h1 style={{ 
            fontSize: 'var(--md-sys-typescale-headline-large-size)',
            fontWeight: 'var(--md-sys-typescale-headline-large-weight)',
            color: 'var(--md-sys-color-on-surface)',
            margin: '0 0 8px 0'
          }}>
            Bible Challenge Leaderboards
          </h1>
          <p style={{ 
            margin: '0',
            fontSize: '14px',
            color: 'var(--md-sys-color-on-surface-variant)'
          }}>
            View rankings for all your challenges
          </p>
        </div>

        {/* Scrollable Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          padding: '24px 16px'
        }}>
          {!loadedChallenges ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px 16px'
            }}>
              <md-circular-progress indeterminate></md-circular-progress>
              <p style={{ 
                marginTop: '16px', 
                color: 'var(--md-sys-color-on-surface-variant)',
                fontSize: '14px'
              }}>
                Loading challenges...
              </p>
            </div>
          ) : challengeList.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: 'var(--md-sys-color-on-surface-variant)'
            }}>
              <Icon name="leaderboard" size="hero" style={{ marginBottom: '16px' }} />
              <p style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
                No challenges found
              </p>
              <p style={{ margin: '0', fontSize: '14px' }}>
                Join a challenge to see leaderboards
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {challengeList.map((challenge) => (
                <ChallengeLeaderboard
                  key={challenge.id}
                  challengeName={challenge.name}
                  challengeId={challenge.id}
                  challengePortions={challenge.portion}
                  isStandalone={false} // This will be used in tab mode
                  challengeType={challenge.challengeType}
                  goalPercent={challenge.goalPercent}
                  datesToChapters={challenge.datesToChapters}
                  isChallengeAdmin={challenge.isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
